import { useContext } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * Generic membership conditional : renders `then` when `value` is in the array
 * `list`, else `else` (either may be omitted → renders nothing). Comparison is
 * loose-by-string (numeric id matches a string key, like the facet primitives).
 *
 * reactive-json has `Hide` (hide on a boolean) but no array-membership test, and
 * YAML can't express "is X in this list" — hence this small primitive. Reusable
 * for a favourite star (filled/empty), a "favourites only" filter, an
 * active-chip highlight, etc.
 *
 * YAML :
 *   - type: InList
 *     value: ~.id
 *     list: ~~.favs
 *     then: { type: button, content: "★", ... }
 *     else: { type: button, content: "☆", ... }
 */
export const InList = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const value = resolve(props.value)
  const list = resolve(props.list)
  // Tolère une liste scalaire (traitée comme un singleton) ou vide.
  const arr = Array.isArray(list)
    ? list
    : (list === null || list === undefined || list === '' ? [] : [list])
  const member = arr.some((v) => String(v) === String(value))

  const key = member ? 'then' : 'else'
  const branch = props[key]
  if (branch === undefined || branch === null) return null

  return (
    <View
      currentData={currentData?.[key]}
      datafield={key}
      path={`${path}.${key}`}
      props={branch}
    />
  )
}
